import * as React from 'react'
import {
  useApproveTabularConfigDraftMutation,
  useArchiveIngestMappingConfigMutation,
  useBeginTabularConfigBuildMutation,
  useCancelIngestJobMutation,
  useGetAuthzMeQuery,
  useListIngestIntakesQuery,
  useRejectTabularConfigDraftMutation,
  useReverifyTabularConfigDraftMutation,
  useReviseIngestMappingConfigMutation,
  useReviseTabularConfigDraftMutation,
  useUnarchiveIngestMappingConfigMutation,
  useUploadTabularConfigBuildFilesMutation,
  type IngestMappingConfigSummary,
  type OutputTable,
  type TabularConfigBuildDetail,
  type TabularColumnDescriptionEdit,
  type TabularConfigDraft,
  type TabularConfigDraftOverrides
} from '@axilon-apps/domains-ontology-ui-state'
import { Loader2 } from 'lucide-react'
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams
} from 'react-router-dom'
import { Button } from '../../components/ui/button'
import { Card } from '../../components/ui/card'
import { useDebugMode } from '../../lib/debug-mode'
import { cn } from '../../lib/utils'
import { NoteDialog } from '../IngestPage/shared/NoteDialog'
import { WriteButton } from '../IngestPage/shared/WriteButton'
import { CHIP, CHIP_WARN, COMPACT_ACTION, COMPACT_PAGE } from '../IngestPage/shared/compact'
import { extractErrorMessage } from '../IngestPage/util/extractErrorMessage'
import {
  tabularConfigBuildPath,
  tabularConfigPath,
  tabularJobPath
} from '../TabularIngestJobPage/route'
import { configBuildPill } from '../TabularIngestJobPage/tabularJobPill'
import { BuildPanel, StopBuildButton, ToolCallsLink } from './BuildPanel'
import { ConfigSpine } from './ConfigSpine'
import { ConfigTabs } from './ConfigTabs'
import { ArchivedBanner } from './ArchivedBanner'
import { DraftBanner } from './DraftBanner'
import { EditMenu, type EditAction } from './EditMenu'
import { ResumedRunPill } from './ResumedRunPill'
import { ArchiveConfigDialog } from './dialogs/ArchiveConfigDialog'
import { UnarchiveConfigDialog } from './dialogs/UnarchiveConfigDialog'
import { ContractEditorDialog } from './dialogs/ContractEditorDialog'
import { EditChecksDialog } from './dialogs/EditChecksDialog'
import { EditDescriptionDialog } from './dialogs/EditDescriptionDialog'
import { InstructionDialog } from './dialogs/InstructionDialog'
import { ReviseDraftDialog, type ReviseDraftSubmit } from './dialogs/ReviseDraftDialog'
import { SaveDraftDialog } from './dialogs/SaveDraftDialog'
import { useConfigPageData } from './useConfigPageData'
import {
  activeVersions,
  defaultTab,
  isBuildInFlight,
  latestActiveVersion,
  newestFirst,
  switcherOptions,
  versionsToUnarchive,
  type ConfigTab,
  type ConfigView
} from './util/configView'
import { builderContractChange, changedSections } from './util/draftChanges'
import {
  acceptedTypesChange,
  buildingMeta,
  draftMeta,
  formatWhen,
  refusingIntakesWarning,
  versionMeta
} from './util/formatMeta'
import { subjectForDraft, subjectForVersion } from './util/subject'

type Dialog =
  | 'checks'
  | 'description'
  | 'instruction'
  | 'contract'
  | 'archive'
  | 'unarchive'
  | 'reject'
  | 'revise'
  | 'save'
  | null

type DraftReviewEdits = TabularConfigDraftOverrides & { output?: { tables: OutputTable[] } }

/**
 * Serves two routes. `/ingest/configs/:name` is the config: its saved
 * versions (`?version=`), History's compare (`?compare=`), and a banner for
 * any build in flight. `/ingest/configs/:name/builds/:buildJobId` is one
 * build, keyed by job id so the URL survives refresh and the build finishing:
 * it shows the live log, then the draft for review, then the saved version
 * (or what ended it) without navigating.
 */
export default function TabularConfigPage() {
  const debugMode = useDebugMode()
  const { name = '', buildJobId } = useParams<{ name: string; buildJobId?: string }>()
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const backTo = (location.state as { from?: string } | null)?.from ?? '/ingest'
  // Where the user came from rides along on every move within the config, so
  // Back (and Reject's return) still lead there after a tab, version or draft.
  const navState = location.state as unknown
  const authzQuery = useGetAuthzMeQuery()
  const canManage = authzQuery.data?.permissions.includes('ingest/manage') === true
  const data = useConfigPageData(name, buildJobId, true)
  const intakesQuery = useListIngestIntakesQuery(undefined, { skip: data.view?.kind !== 'draft' })

  const [dialog, setDialog] = React.useState<Dialog>(null)
  const [actionError, setActionError] = React.useState<string | null>(null)
  const [busy, setBusy] = React.useState(false)
  const [note, setNote] = React.useState('')
  const [edits, setEdits] = React.useState<DraftReviewEdits>({})
  // Reviewer edits belong to one draft: a new build (or a re-verified draft) drops them.
  const draftDigest = data.view?.kind === 'draft' ? data.view.draft.draftDigest : undefined
  React.useEffect(() => setEdits({}), [draftDigest])
  // An error raised on one build that is about the build it navigates to.
  const carriedError = React.useRef<string | null>(null)
  // A dialog, its note and its error belong to the config or build they were opened on.
  React.useEffect(() => {
    setDialog(null)
    setActionError(carriedError.current)
    carriedError.current = null
    setNote('')
  }, [name, buildJobId])

  const [approve] = useApproveTabularConfigDraftMutation()
  const [reverify] = useReverifyTabularConfigDraftMutation()
  const [reject] = useRejectTabularConfigDraftMutation()
  const [reviseDraft] = useReviseTabularConfigDraftMutation()
  const [revise] = useReviseIngestMappingConfigMutation()
  const [beginBuild] = useBeginTabularConfigBuildMutation()
  const [uploadFiles] = useUploadTabularConfigBuildFilesMutation()
  const [cancelIngestJob] = useCancelIngestJobMutation()
  const [archive] = useArchiveIngestMappingConfigMutation()
  const [unarchive] = useUnarchiveIngestMappingConfigMutation()

  const run = async (fallback: string, action: () => Promise<void>) => {
    setActionError(null)
    setBusy(true)
    try {
      await action()
      setDialog(null)
    } catch (error) {
      setActionError(staleOr(extractErrorMessage(error, fallback)))
    } finally {
      setBusy(false)
    }
  }
  const closeDialog = (open: boolean) => {
    if (!open) {
      setDialog(null)
      setActionError(null)
    }
  }

  const { view } = data
  if (view?.kind === 'redirect') return <Navigate to={view.to} replace state={navState} />

  const parserKind =
    view && 'config' in view
      ? view.config.definition.parser.kind
      : view?.kind === 'draft'
        ? view.draft.parser.kind
        : 'script'
  const tab = defaultTab(parserKind, params.get('tab'))
  const setTab = (next: ConfigTab) => {
    const search = new URLSearchParams(params)
    if (next === 'tables') search.delete('tab')
    else search.set('tab', next)
    setParams(search, { replace: true, state: navState })
  }
  const inFlight = data.build && isBuildInFlight(data.build) ? data.build : null
  const options = switcherOptions({ name, versions: data.versions, inFlight })
  const switcherValue =
    view?.kind === 'version' ? String(view.config.version) : buildJobId ? `build:${buildJobId}` : ''
  if (
    buildJobId &&
    view &&
    view.kind !== 'version' &&
    view.kind !== 'empty' &&
    !options.some((o) => o.value === switcherValue)
  ) {
    options.unshift({
      value: switcherValue,
      label: `draft v${view.build.proposedVersion}${view.kind === 'ended' ? ` · ${view.outcome}` : ''}`,
      href: tabularConfigBuildPath(name, buildJobId)
    })
  }
  const switcher = {
    value: switcherValue,
    options,
    onChange: (value: string) => {
      const option = options.find((o) => o.value === value)
      if (option?.href) navigate(option.href, { state: navState })
      else
        navigate(
          tabularConfigPath(name, { version: Number(value), ...(tab !== 'tables' ? { tab } : {}) }),
          { state: navState }
        )
    }
  }

  if (data.loading || !view) {
    return (
      <Shell name={name} backTo={backTo}>
        {data.error ? (
          <p role="alert" className="text-xs text-st-down">
            Could not load this config.
          </p>
        ) : (
          <p className="text-xs text-ink-3">Loading config…</p>
        )}
      </Shell>
    )
  }
  if (view.kind === 'empty') {
    return (
      <Shell name={name} backTo={backTo}>
        <Card className="p-4 text-xs text-ink-3">No config named {name}.</Card>
      </Shell>
    )
  }

  if (view.kind === 'building' || view.kind === 'ended') {
    const pill = configBuildPill(view.build.state, view.build.result.phase)
    const latest = latestActiveVersion(data.versions)
    return (
      <div className={COMPACT_PAGE}>
        <ConfigSpine
          name={name}
          backTo={backTo}
          pill={pill}
          switcher={switcher}
          meta={view.kind === 'building' ? buildingMeta(view.build) : undefined}
          actions={
            <>
              {debugMode ? <ToolCallsLink name={name} build={view.build} /> : null}
              {view.kind === 'building' ? (
                <StopBuildButton name={name} build={view.build} />
              ) : latest ? (
                <Button asChild size="sm" variant="outline" className={COMPACT_ACTION}>
                  <Link to={tabularConfigPath(name)} state={navState}>
                    View v{latest.version}
                  </Link>
                </Button>
              ) : (
                <Button asChild size="sm" variant="outline" className={COMPACT_ACTION}>
                  <Link to="/ingest?src=tabular&action=add">Start a new build</Link>
                </Button>
              )}
            </>
          }
        >
          {actionError ? (
            <p role="alert" className="text-xs text-st-down">
              {actionError}
            </p>
          ) : null}
        </ConfigSpine>
        <BuildPanel build={view.build} outcome={view.kind === 'ended' ? view.outcome : undefined} />
      </div>
    )
  }

  if (view.kind === 'draft') {
    const { build, draft } = view
    const acceptedTypesSummary = acceptedTypesChange(draft, view.base)
    const intakeWarning = refusingIntakesWarning(name, draft, intakesQuery.data?.intakes ?? [])
    const subject = subjectForDraft(build, draft, view.base)
    const editedSubject = edits.output ? { ...subject, output: edits.output } : subject
    const pill = configBuildPill(build.state, build.result.phase)
    const changed = [...changedSections(subject.diff)]
    const contractChange = builderContractChange(draft.mode, build.reason.kind, changed)
    const contractWarning = contractChange
      ? `The builder changed the ${contractChange} from ${
          subject.compareLabel ?? 'the previous version'
        }. Review the ${
          contractChange === 'tables and checks'
            ? 'Tables and Checks tabs'
            : contractChange === 'tables'
              ? 'Tables tab'
              : 'Checks tab'
        } before saving — later uploads use the new ${contractChange}.`
      : undefined
    const edited = Object.keys(edits)
    // While an approval is being committed the draft is read-only: it becomes
    // a version in a moment, or comes back to review with the error.
    const saving = view.saving === true || busy
    const agentEditsNeedVerification = draft.parser.kind === 'agent' && edited.length > 0
    const parentJobId = 'parentJobId' in build.reason ? build.reason.parentJobId : undefined
    // Only a build that uploaded example files has a choice to make on save:
    // whether their rows are ingested too. The others save straight away.
    const uploadedExamples =
      build.reason.kind === 'initial' ||
      build.reason.kind === 'new_examples' ||
      (build.reason.kind === 'instruction' && addsExamples(draft, view.base))
    const onApprove = (choice: { ingestExamples: boolean } = { ingestExamples: true }) =>
      run('The draft could not be saved.', async () => {
        await approve({
          buildJobId: build.jobId,
          draftDigest: draft.draftDigest,
          ...(edited.length ? { overrides: edits } : {}),
          ...(choice.ingestExamples ? {} : { ingestExamples: false })
        }).unwrap()
        setEdits({})
        await data.refetchBuild()
      })
    const onReject = () =>
      run('The draft could not be rejected.', async () => {
        await reject({
          buildJobId: build.jobId,
          draftDigest: draft.draftDigest,
          note: note.trim()
        }).unwrap()
        setNote('')
        if (backTo !== '/ingest') navigate(backTo)
        else await data.refetchBuild()
      })
    const onRevise = async (input: ReviseDraftSubmit) => {
      setActionError(null)
      setBusy(true)
      try {
        const revised = await reviseDraft({
          buildJobId: build.jobId,
          draftDigest: draft.draftDigest,
          note: input.note,
          ...(input.tables ? { requestedContract: { output: { tables: input.tables } } } : {})
        }).unwrap()
        setDialog(null)
        navigate(tabularConfigBuildPath(name, revised.buildJobId), { state: navState })
      } catch (error) {
        const revision = revisionConflict(error)
        if (revision) {
          const sentence = `This draft was revised by ${revision.revisedBy}${
            revision.revisedAt ? ` at ${formatWhen(revision.revisedAt)}` : ''
          }.`
          setDialog(null)
          await data.refetchBuild()
          if (revision.supersededBy) {
            carriedError.current = sentence
            navigate(tabularConfigBuildPath(name, revision.supersededBy), { state: navState })
          } else {
            setActionError(sentence)
          }
          return
        }
        const message = extractErrorMessage(error, 'The draft could not be sent back.')
        setActionError(staleOr(message))
        // Someone else approved or rejected it meanwhile: show what it is now.
        if (isConflict(error) && !/STALE_CONFIG_VERSION/.test(message)) await data.refetchBuild()
      } finally {
        setBusy(false)
      }
    }
    const onReverify = () =>
      run('The edited draft could not be verified.', async () => {
        const verified = await reverify({
          buildJobId: build.jobId,
          draftDigest: draft.draftDigest,
          description: edits.description ?? draft.description,
          output: edits.output ?? draft.output,
          checks: edits.checks ?? draft.checks
        }).unwrap()
        setEdits({})
        if (verified.draft.draftDigest === draft.draftDigest) {
          throw new Error('Verification did not produce a new reviewed draft.')
        }
        await data.refetchBuild()
      })
    return (
      <div className={COMPACT_PAGE}>
        <ConfigSpine
          name={name}
          backTo={backTo}
          pill={pill}
          switcher={switcher}
          meta={draftMeta(build, draft)}
          actions={
            <>
              {debugMode ? <ToolCallsLink name={name} build={build} /> : null}
              <WriteButton
                canManage={canManage}
                size="sm"
                variant="outline"
                className={COMPACT_ACTION}
                disabled={saving || agentEditsNeedVerification}
                onClick={() => setDialog('reject')}
                data-testid="draft-reject"
              >
                Reject…
              </WriteButton>
              <WriteButton
                canManage={canManage}
                size="sm"
                variant="outline"
                className={COMPACT_ACTION}
                disabled={saving}
                onClick={() => setDialog('revise')}
                data-testid="draft-revise"
              >
                Send back with changes…
              </WriteButton>
              <WriteButton
                canManage={canManage}
                size="sm"
                className={COMPACT_ACTION}
                disabled={saving || agentEditsNeedVerification}
                onClick={() =>
                  uploadedExamples || contractWarning || intakeWarning
                    ? setDialog('save')
                    : onApprove()
                }
                data-testid="draft-approve"
              >
                {saving ? <Loader2 className="size-3 animate-spin" aria-hidden /> : null}
                {saving ? 'Saving…' : `Save as v${draft.proposedVersion}`}
              </WriteButton>
              {agentEditsNeedVerification ? (
                <WriteButton
                  canManage={canManage}
                  size="sm"
                  className={COMPACT_ACTION}
                  disabled={saving}
                  onClick={onReverify}
                  data-testid="draft-reverify"
                >
                  {saving ? <Loader2 className="size-3 animate-spin" aria-hidden /> : null}
                  Apply and re-run trial
                </WriteButton>
              ) : null}
            </>
          }
        >
          <div
            className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-11 text-ink-2"
            data-testid="draft-summary"
          >
            {changed.length ? (
              <>
                <span>Changed from {subject.compareLabel ?? 'nothing'}:</span>
                {changed.map((section) => (
                  <span key={section} className={cn(CHIP, CHIP_WARN)}>
                    {section}
                  </span>
                ))}
              </>
            ) : draft.mode === 'update' && !acceptedTypesSummary ? (
              <span>Nothing changed from {subject.compareLabel ?? 'the previous version'}.</span>
            ) : null}
            {edited.length ? (
              <>
                <span className="ml-1">Your unsaved edits:</span>
                {edited.map((field) => (
                  <span
                    key={field}
                    className={cn(CHIP, 'border-st-info/40 bg-st-info-bg text-st-info')}
                  >
                    {field}
                  </span>
                ))}
              </>
            ) : null}
            {acceptedTypesSummary ? <span>{acceptedTypesSummary}</span> : null}
            {parentJobId ? (
              <span className="ml-1">
                Saving re-runs{' '}
                <Link className="underline" to={tabularJobPath(parentJobId)}>
                  the held job
                </Link>{' '}
                and every run that joined this update.
              </span>
            ) : null}
          </div>
          {contractWarning ? (
            <p role="note" className="text-xs text-st-warn" data-testid="draft-contract-warning">
              {contractWarning}
            </p>
          ) : null}
          {intakeWarning ? (
            <p role="note" className="text-xs text-st-warn" data-testid="draft-intake-warning">
              {intakeWarning}
            </p>
          ) : null}
          {actionError && !dialog ? (
            <p role="alert" className="text-xs text-st-down">
              {actionError}
            </p>
          ) : null}
        </ConfigSpine>
        <ConfigTabs
          name={name}
          subject={editedSubject}
          tab={tab}
          onTabChange={setTab}
          navState={navState}
          versions={data.versions}
          inFlight={build}
          reason={build.reason}
          edits={edits}
          canManage={canManage}
          editable={!saving}
          onEditOutput={draft.parser.kind === 'agent' ? () => setDialog('contract') : undefined}
          onEditChecks={() => setDialog('checks')}
          onEditDescription={() => setDialog('description')}
        />
        <EditChecksDialog
          open={dialog === 'checks'}
          onOpenChange={closeDialog}
          mode="overrides"
          tables={subject.output.tables.map((table) => table.table)}
          initial={edits.checks ?? subject.checks}
          loading={false}
          onSubmit={({ checks }) => {
            setEdits((current) => ({ ...current, checks }))
            setDialog(null)
          }}
        />
        <EditDescriptionDialog
          open={dialog === 'description'}
          onOpenChange={closeDialog}
          mode="overrides"
          initial={edits.description ?? subject.description}
          tables={(edits.output ?? draft.output).tables}
          loading={false}
          onSubmit={({ description, columnDescriptions }) => {
            setEdits((current) => ({
              ...current,
              description,
              ...(columnDescriptions?.length
                ? {
                    output: {
                      tables: applyColumnDescriptions(
                        current.output?.tables ?? draft.output.tables,
                        columnDescriptions
                      )
                    }
                  }
                : {})
            }))
            setDialog(null)
          }}
        />
        <ContractEditorDialog
          open={dialog === 'contract'}
          onOpenChange={closeDialog}
          initial={(edits.output ?? draft.output).tables}
          loading={false}
          mode="draft"
          {...(draft.parser.kind === 'agent' && draft.parser.finalize
            ? { finalizeEntrypoint: draft.parser.finalize.entrypoint }
            : {})}
          onSubmit={({ tables }) => {
            setEdits((current) => ({ ...current, output: { tables } }))
            setDialog(null)
          }}
        />
        <SaveDraftDialog
          open={dialog === 'save'}
          onOpenChange={closeDialog}
          configName={name}
          proposedVersion={draft.proposedVersion}
          askIngestExamples={uploadedExamples}
          contractWarning={contractWarning}
          intakeWarning={intakeWarning}
          loading={busy}
          error={actionError ?? undefined}
          onSubmit={onApprove}
        />
        <NoteDialog
          open={dialog === 'reject'}
          onOpenChange={closeDialog}
          title={`Reject draft v${draft.proposedVersion}?`}
          description="Say what is wrong. The next rebuild of this version will see your note. The held job, if any, fails with it — Rebuild with agent on the job page starts a new build."
          note={note}
          onNoteChange={setNote}
          actionLabel="Reject draft"
          required
          loading={busy}
          error={actionError ?? undefined}
          onSubmit={onReject}
        />
        <ReviseDraftDialog
          open={dialog === 'revise'}
          onOpenChange={closeDialog}
          draft={draft}
          parserKind={draft.parser.kind}
          loading={busy}
          error={actionError ?? undefined}
          onSubmit={(input) => void onRevise(input)}
        />
      </div>
    )
  }

  return (
    <VersionPage
      name={name}
      view={view}
      versions={data.versions}
      inFlight={inFlight}
      backTo={backTo}
      navState={navState}
      switcher={switcher}
      tab={tab}
      setTab={setTab}
      compare={params.get('compare')}
      canManage={canManage}
      debugMode={debugMode}
      dialog={dialog}
      setDialog={setDialog}
      closeDialog={closeDialog}
      busy={busy}
      actionError={actionError}
      run={run}
      actions={{
        revise: async (input) => {
          const revised = await revise(input).unwrap()
          navigate(
            tabularConfigPath(name, {
              version: revised.version,
              ...(tab !== 'tables' ? { tab } : {})
            }),
            { state: navState }
          )
        },
        begin: async (input, files) => {
          const started = await beginBuild(input).unwrap()
          if (files.length) {
            try {
              await uploadFiles({ jobId: started.jobId, files }).unwrap()
            } catch (error) {
              // A build whose examples never arrived can only sit there and
              // refuse the next one as "already building": drop it.
              await cancelIngestJob(started.jobId)
                .unwrap()
                .catch(() => undefined)
              throw error
            }
          }
          navigate(tabularConfigBuildPath(name, started.jobId), {
            state: { from: location.pathname }
          })
        },
        archive: async (ids) => {
          for (const id of ids) await archive(id).unwrap()
          data.refetchAll()
        },
        unarchive: async (ids) => {
          for (const id of ids) await unarchive(id).unwrap()
          data.refetchAll()
        },
        retryExampleIngest: async (build) => {
          await approve({
            buildJobId: build.jobId,
            draftDigest: build.result.draft?.draftDigest ?? '',
            ingestExamples: true
          }).unwrap()
          await data.refetchBuild()
        }
      }}
    />
  )
}

function applyColumnDescriptions(
  tables: OutputTable[],
  edits: TabularColumnDescriptionEdit[]
): OutputTable[] {
  const byColumn = new Map(edits.map((edit) => [`${edit.table}\0${edit.column}`, edit.description]))
  return tables.map((table) => ({
    ...table,
    columns: table.columns.map((column) => {
      const description = byColumn.get(`${table.table}\0${column.name}`)
      return description === undefined ? column : { ...column, description }
    })
  }))
}

function Shell({
  name,
  backTo,
  children
}: {
  name: string
  backTo: string
  children: React.ReactNode
}) {
  return (
    <div className={COMPACT_PAGE}>
      <ConfigSpine name={name} backTo={backTo} />
      {children}
    </div>
  )
}

/** 409 from a concurrent save, said the way the plan words it. */
function staleOr(message: string): string {
  return /TABULAR_CONFIG_DRAFT_CHANGED|TABULAR_CONFIG_REVERIFY_CONFLICT/.test(message)
    ? 'This draft changed while you were editing. Reload the latest draft and try again.'
    : /STALE_CONFIG_VERSION/.test(message)
      ? 'Someone saved a newer version while you were editing. Reload and try again.'
      : message
}

function isConflict(error: unknown): boolean {
  return (error as { status?: unknown } | null)?.status === 409
}

interface RevisionConflict {
  supersededBy?: string
  revisedBy: string
  revisedAt: string
}

/** The revise route's 409 when another reviewer sent the same draft back first. */
function revisionConflict(error: unknown): RevisionConflict | undefined {
  if (!isConflict(error)) return undefined
  const data = (error as { data?: unknown }).data as
    | { error?: unknown; revision?: Partial<RevisionConflict> }
    | undefined
  if (data?.error !== 'TABULAR_CONFIG_REVISION_CONFLICT') return undefined
  const revision = data.revision
  return {
    ...(typeof revision?.supersededBy === 'string' ? { supersededBy: revision.supersededBy } : {}),
    revisedBy: typeof revision?.revisedBy === 'string' ? revision.revisedBy : 'someone',
    revisedAt: typeof revision?.revisedAt === 'string' ? revision.revisedAt : ''
  }
}

/** An instruction build may carry extra example files; those, like any upload, can be ingested on save. */
function addsExamples(draft: TabularConfigDraft, base?: IngestMappingConfigSummary): boolean {
  const stored = new Set(base?.definition.examples.map((example) => example.sha256) ?? [])
  return draft.examples.some((example) => !stored.has(example.sha256))
}

interface VersionActions {
  revise: (
    input: Parameters<ReturnType<typeof useReviseIngestMappingConfigMutation>[0]>[0]
  ) => Promise<void>
  begin: (
    input: Parameters<ReturnType<typeof useBeginTabularConfigBuildMutation>[0]>[0],
    files: File[]
  ) => Promise<void>
  archive: (ids: string[]) => Promise<void>
  unarchive: (ids: string[]) => Promise<void>
  /** Approve the saved build again: the service retries only the example run that failed to start. */
  retryExampleIngest: (build: TabularConfigBuildDetail) => Promise<void>
}

function VersionPage({
  name,
  view,
  versions,
  inFlight,
  backTo,
  navState,
  switcher,
  tab,
  setTab,
  compare,
  canManage,
  debugMode,
  dialog,
  setDialog,
  closeDialog,
  busy,
  actionError,
  run,
  actions
}: {
  name: string
  view: Extract<ConfigView, { kind: 'version' }>
  versions: IngestMappingConfigSummary[]
  inFlight: TabularConfigBuildDetail | null
  backTo: string
  /** `location.state` to carry on links within the config. */
  navState: unknown
  switcher: React.ComponentProps<typeof ConfigSpine>['switcher']
  tab: ConfigTab
  setTab: (tab: ConfigTab) => void
  compare: string | null
  canManage: boolean
  debugMode: boolean
  dialog: Dialog
  setDialog: (dialog: Dialog) => void
  closeDialog: (open: boolean) => void
  busy: boolean
  actionError: string | null
  run: (fallback: string, action: () => Promise<void>) => Promise<void>
  actions: VersionActions
}) {
  const { config, latest, banner } = view
  const compareTo =
    compare !== null ? versions.find((row) => row.version === Number(compare)) : undefined
  const subject = subjectForVersion(config, compareTo)
  const { definition } = config
  const isLatest = latest?.id === config.id && config.state === 'active'
  const archived = config.state === 'archived'
  const unarchiveRows = archived
    ? versionsToUnarchive(
        versions,
        versions.some((row) => row.state === 'active') ? { version: config.version } : {}
      )
    : []
  const buildInFlight = inFlight !== null
  const resumedJobId =
    banner?.kind === 'draft_saved' && 'parentJobId' in banner.build.reason
      ? banner.build.reason.parentJobId
      : undefined
  const onEdit = (action: EditAction) => setDialog(action)
  return (
    <div className={COMPACT_PAGE}>
      <ConfigSpine
        name={name}
        backTo={backTo}
        switcher={switcher}
        meta={versionMeta(config)}
        {...(archived ? { pill: { status: 'idle' as const, label: 'archived' } } : {})}
        actions={
          <>
            {debugMode && banner ? <ToolCallsLink name={name} build={banner.build} /> : null}
            {unarchiveRows.length > 0 ? (
              <WriteButton
                canManage={canManage}
                size="sm"
                variant="outline"
                className={COMPACT_ACTION}
                disabled={busy}
                onClick={() => setDialog('unarchive')}
                data-testid="unarchive-config"
              >
                Unarchive
              </WriteButton>
            ) : null}
            {isLatest ? (
              <EditMenu
                parserKind={definition.parser.kind}
                canManage={canManage}
                buildInFlight={buildInFlight}
                onAction={onEdit}
              />
            ) : archived ? null : (
              <span className="text-11 text-ink-3" data-testid="not-latest-note">
                Viewing v{config.version}
                {latest && latest.id !== config.id ? (
                  <>
                    {' '}
                    · edits start from{' '}
                    <Link className="underline" to={tabularConfigPath(name)} state={navState}>
                      v{latest.version}
                    </Link>
                  </>
                ) : null}
              </span>
            )}
          </>
        }
      >
        {archived ? (
          <ArchivedBanner
            name={name}
            config={config}
            navState={navState}
            {...(latest?.state === 'active' ? { latestActive: latest } : {})}
          />
        ) : null}
        {banner ? (
          <DraftBanner
            name={name}
            banner={banner}
            navState={navState}
            busy={busy}
            {...(canManage
              ? {
                  onRetryExampleIngest: () =>
                    run('The example ingest could not be started.', () =>
                      actions.retryExampleIngest(banner.build)
                    )
                }
              : {})}
          />
        ) : null}
        {resumedJobId ? <ResumedRunPill jobId={resumedJobId} /> : null}
        {compareTo ? (
          <p className="text-11 text-ink-2" data-testid="compare-note">
            Comparing v{config.version} with v{compareTo.version} —{' '}
            <Link
              className="underline"
              to={tabularConfigPath(name, { version: config.version, tab })}
              state={navState}
            >
              stop comparing
            </Link>
          </p>
        ) : null}
        {actionError && !dialog ? (
          <p role="alert" className="text-xs text-st-down">
            {actionError}
          </p>
        ) : null}
      </ConfigSpine>
      <ConfigTabs
        name={name}
        subject={subject}
        tab={tab}
        onTabChange={setTab}
        navState={navState}
        versions={versions}
        inFlight={inFlight}
        currentVersion={config.version}
        canManage={canManage}
        editable={isLatest}
        onEditChecks={() => setDialog('checks')}
        onEditDescription={() => setDialog('description')}
      />
      <EditChecksDialog
        open={dialog === 'checks'}
        onOpenChange={closeDialog}
        mode="revise"
        tables={definition.output.tables.map((table) => table.table)}
        initial={definition.checks}
        loading={busy}
        error={actionError ?? undefined}
        onSubmit={({ checks, changeReason }) =>
          void run('The checks could not be saved.', () =>
            actions.revise({ id: config.id, changeReason: changeReason ?? 'checks edited', checks })
          )
        }
      />
      <EditDescriptionDialog
        open={dialog === 'description'}
        onOpenChange={closeDialog}
        mode="revise"
        parserKind={definition.parser.kind}
        exampleCount={definition.examples.length}
        initial={definition.description}
        tables={
          definition.parser.kind === 'agent' && definition.pageTables
            ? definition.pageTables
            : definition.output.tables
        }
        pageTables={definition.parser.kind === 'agent' && !!definition.pageTables}
        loading={busy}
        error={actionError ?? undefined}
        onSubmit={({ description, columnDescriptions, changeReason, note }) =>
          definition.parser.kind === 'agent'
            ? void run('The description edit could not be started.', () =>
                actions.begin(
                  {
                    mode: 'update',
                    configName: name,
                    configId: config.id,
                    configVersion: config.version,
                    reason: { kind: 'description_edit', note: note ?? '' },
                    description,
                    ...(columnDescriptions?.length ? { columnDescriptions } : {}),
                    files: []
                  },
                  []
                )
              )
            : void run('The description could not be saved.', () =>
                actions.revise({
                  id: config.id,
                  changeReason: changeReason ?? 'description edited',
                  description,
                  ...(columnDescriptions?.length ? { columnDescriptions } : {})
                })
              )
        }
      />
      <InstructionDialog
        open={dialog === 'instruction'}
        onOpenChange={closeDialog}
        parserKind={definition.parser.kind}
        version={config.version}
        storedExamples={definition.examples.map((example) => example.name)}
        loading={busy}
        error={actionError ?? undefined}
        onSubmit={({ note, files, exampleStrategy }) =>
          void run('The update build could not be started.', () =>
            actions.begin(
              {
                mode: 'update',
                configName: name,
                configId: config.id,
                configVersion: config.version,
                reason: { kind: 'instruction', note },
                exampleStrategy,
                files: files.map((file) => ({ name: file.name, bytes: file.size }))
              },
              files
            )
          )
        }
      />
      <ContractEditorDialog
        open={dialog === 'contract'}
        onOpenChange={closeDialog}
        initial={definition.output.tables}
        loading={busy}
        error={actionError ?? undefined}
        parserKind={definition.parser.kind}
        {...(definition.parser.kind === 'agent' && definition.parser.finalize
          ? { finalizeEntrypoint: definition.parser.finalize.entrypoint }
          : {})}
        onSubmit={({ tables, note }) =>
          void run('The update build could not be started.', () =>
            actions.begin(
              {
                mode: 'update',
                configName: name,
                configId: config.id,
                configVersion: config.version,
                reason: { kind: 'contract_edit', note },
                requestedContract: { output: { tables } },
                files: []
              },
              []
            )
          )
        }
      />
      <ArchiveConfigDialog
        open={dialog === 'archive'}
        onOpenChange={closeDialog}
        name={name}
        activeVersions={activeVersions(versions).length}
        loading={busy}
        error={actionError ?? undefined}
        onConfirm={() =>
          void run('The config could not be archived.', () =>
            actions.archive(newestFirst(activeVersions(versions)).map((row) => row.id))
          )
        }
      />
      <UnarchiveConfigDialog
        open={dialog === 'unarchive'}
        onOpenChange={closeDialog}
        name={name}
        versionCount={unarchiveRows.length}
        loading={busy}
        error={actionError ?? undefined}
        onConfirm={() =>
          void run('The config could not be unarchived.', () =>
            actions.unarchive(unarchiveRows.map((row) => row.id))
          )
        }
      />
    </div>
  )
}
